import { type BusinessProfileLink, ORGANIZER_LINK_CODE_LENGTH } from '@pulsify/shared/dto'
import { Smartphone, Unlink } from 'lucide-react'
import { type FormEvent, useId, useState } from 'react'
import { toast } from 'sonner'
import { useCurrentBusiness } from '~/entities/business/model/use-business'
import { PROFILE_LINK_ANCHOR } from '~/entities/profile-link/model/profile-link-anchor'
import {
  useLinkProfile,
  useProfileLinks,
  useUnlinkProfile,
} from '~/entities/profile-link/model/use-profile-links'
import * as m from '~/paraglide/messages'
import { getErrorMessageFromError } from '~/shared/lib/api-errors'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '~/shared/ui/alert-dialog'
import { Avatar, AvatarFallback } from '~/shared/ui/avatar'
import { Button } from '~/shared/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '~/shared/ui/card'
import { Input } from '~/shared/ui/input'
import { Label } from '~/shared/ui/label'
import { Skeleton } from '~/shared/ui/skeleton'

const DIGITS_ONLY = /\D/g

/** "Pulsify-Profil verbinden": the steps in the app, the code field and the connected profiles. */
export function ProfileLinkCard({ className }: { className?: string }) {
  const { data: business } = useCurrentBusiness()
  const businessId = business?.id ?? ''
  const links = useProfileLinks(businessId)
  const link = useLinkProfile(businessId)
  const codeId = useId()
  const [code, setCode] = useState('')
  const [unlinking, setUnlinking] = useState<BusinessProfileLink | null>(null)
  const ready = code.length === ORGANIZER_LINK_CODE_LENGTH && !link.isPending && !!businessId

  const submit = (event: FormEvent) => {
    event.preventDefault()
    if (!ready) return
    link.mutate(code, {
      onSuccess: ({ link: linked }) => {
        setCode('')
        toast.success(m.profileLinkDone({ name: linked.displayName ?? linked.username ?? '' }))
      },
      onError: (error) =>
        toast.error(m.profileLinkFailed(), { description: getErrorMessageFromError(error) }),
    })
  }

  return (
    <Card id={PROFILE_LINK_ANCHOR} className={className}>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Smartphone className="size-5" />
          {m.profileLinkTitle()}
        </CardTitle>
        <CardDescription>{m.profileLinkDescription()}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <ol className="list-inside list-decimal space-y-1 text-sm">
          <li>{m.profileLinkStepOpen()}</li>
          <li>{m.profileLinkStepSettings()}</li>
          <li>{m.profileLinkStepConnect()}</li>
          <li>{m.profileLinkStepCode()}</li>
        </ol>

        <form onSubmit={submit} className="space-y-2">
          <Label htmlFor={codeId}>{m.profileLinkCodeLabel()}</Label>
          <div className="flex gap-2">
            <Input
              id={codeId}
              value={code}
              onChange={(e) =>
                setCode(
                  e.target.value.replace(DIGITS_ONLY, '').slice(0, ORGANIZER_LINK_CODE_LENGTH),
                )
              }
              inputMode="numeric"
              autoComplete="one-time-code"
              placeholder={m.profileLinkCodePlaceholder()}
              className="w-40 text-center font-mono tracking-widest"
            />
            <Button type="submit" disabled={!ready}>
              {link.isPending ? m.profileLinkConnecting() : m.profileLinkConnect()}
            </Button>
          </div>
          <p className="text-xs text-muted-foreground">{m.profileLinkCodeHint()}</p>
        </form>

        <div className="space-y-2">
          <h4 className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            {m.profileLinkConnected()}
          </h4>
          {links.isLoading ? (
            <Skeleton className="h-12 w-full" />
          ) : (links.data?.links.length ?? 0) === 0 ? (
            <p className="text-sm text-muted-foreground">{m.profileLinkNone()}</p>
          ) : (
            <ul className="divide-y rounded-lg border">
              {links.data?.links.map((profile) => (
                <li key={profile.userProfileId} className="flex items-center gap-3 p-3">
                  <Avatar className="size-9">
                    {profile.avatarUrl ? (
                      <img src={profile.avatarUrl} alt="" className="size-full object-cover" />
                    ) : (
                      <AvatarFallback className="text-sm font-medium">
                        {(profile.displayName ?? profile.username ?? '?').slice(0, 1).toUpperCase()}
                      </AvatarFallback>
                    )}
                  </Avatar>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium">
                      {profile.displayName ?? profile.username}
                    </p>
                    {profile.username ? (
                      <p className="truncate text-xs text-muted-foreground">@{profile.username}</p>
                    ) : null}
                  </div>
                  <Button variant="outline" size="sm" onClick={() => setUnlinking(profile)}>
                    <Unlink className="size-4" />
                    {m.profileLinkDisconnect()}
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </CardContent>
      <UnlinkDialog
        profile={unlinking}
        businessId={businessId}
        onClose={() => setUnlinking(null)}
      />
    </Card>
  )
}

function UnlinkDialog({
  profile,
  businessId,
  onClose,
}: {
  profile: BusinessProfileLink | null
  businessId: string
  onClose: () => void
}) {
  const unlink = useUnlinkProfile(businessId)
  const name = profile?.displayName ?? profile?.username ?? ''

  return (
    <AlertDialog open={!!profile} onOpenChange={(open) => !open && onClose()}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{m.profileLinkDisconnectTitle({ name })}</AlertDialogTitle>
          <AlertDialogDescription>{m.profileLinkDisconnectBody()}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>{m.cancel()}</AlertDialogCancel>
          <AlertDialogAction
            disabled={unlink.isPending}
            onClick={() => {
              if (!profile) return
              unlink.mutate(profile.userProfileId, {
                onSuccess: onClose,
                onError: (error) => toast.error(getErrorMessageFromError(error)),
              })
            }}
          >
            {m.profileLinkDisconnect()}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}
